import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GridViewIcon from '@mui/icons-material/GridView';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { Box, IconButton, InputAdornment, Tab, useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, Button, Badge, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import Fade from 'react-reveal/Fade';
import { useHistory } from 'react-router';
import Swal from 'sweetalert2';
import { changeMember, searchUserByCitizenId, getAllCompanies } from '../../../../api/myCompany';
import {
    getRequestedServicesCitizenIDWithAllCompany,
    getRequestedServicesCitizenIDWithAllCompanyMembers,
    getRequestedServicesCitizenIDWithOutCompany
} from '../../../../api/RequestedServiceList';
import CenterLoading from '../../../../components/CenterLoading/CenterLoading';
import RequestCard from '../../../../components/RequestCard/RequestCard';
import Select from '../../../../components/Select/Select';
import TextField from '../../../../components/TextField/TextField';
import COLORS from '../../../../theme/Colors';
import FONTS_FAMILY from '../../../../theme/FontsFamily';
import { InputFilter, MediumHeightDivider, Row, RowMedia, SmallHeightDivider, StyledPagination } from '../../../../theme/Styles';
import { requestFilterTypes, getRequestFilterTypes } from '../../MyDeskConstants';
import { ListContainer, SectionTitle, RequestsWrapper } from '../../styles/MyDeskStyles';
import DialogChangeApplicant from '../Company/components/DialogChangeApplicant';
import EmptyState from '../../components/EmptyState';
import { replaceGuionToSlashFromString } from '../../../../utilities/functions/StringUtil';

import { REQUEST_STATUS } from '../../../../constants/constants';
import LocalStorageService from '../../../../services/LocalStorageService';

// Estados reales (status.id) que corresponden a cada opción del filtro "Por status",
// usados para saber si una opción tiene solicitudes y debe mostrarse en el desplegable.
const STATUS_IDS_BY_FILTER_TYPE = {
    [REQUEST_STATUS.IN_PROCESS]: [REQUEST_STATUS.IN_PROCESS, 20],
    [REQUEST_STATUS.ACTION_REQUIRED]: [REQUEST_STATUS.ACTION_REQUIRED],
    [REQUEST_STATUS.APPROVED]: [REQUEST_STATUS.APPROVED],
    [REQUEST_STATUS.FINISHED]: [REQUEST_STATUS.CANCELED, REQUEST_STATUS.REJECTED, REQUEST_STATUS.DISMISSED, REQUEST_STATUS.EXPIRED],
};

const matchesDateFilter = (req, dateVal) => {
    if (!dateVal) return true;
    const parts = dateVal.split('-');
    if (parts.length !== 3) return true;
    const [year, month, day] = parts;
    const numYear = parseInt(year, 10);
    const numMonth = parseInt(month, 10);
    const numDay = parseInt(day, 10);

    const matchesDate = (dateString) => {
        if (!dateString) return false;
        const d = new Date(replaceGuionToSlashFromString(dateString));
        if (isNaN(d.getTime())) return false;
        return d.getFullYear() === numYear &&
               (d.getMonth() + 1) === numMonth &&
               d.getDate() === numDay;
    };

    return matchesDate(req?.created_at) || matchesDate(req?.updated_at);
};

const isActionRequiredExpired = (req) => {
    const actions = req?.required_actions || req?.requiredActions || (req?.request_actions ? (Array.isArray(req.request_actions) ? req.request_actions : [req.request_actions]) : []);
    if (!Array.isArray(actions) || actions.length === 0) return false;
    const now = new Date();
    return actions.some(act => {
        if (!act) return false;
        const expStr = act.expiration_date || act.limit_date || act.due_date;
        if (expStr) {
            const d = new Date(String(expStr).replace(/-/g, '/'));
            return !isNaN(d.getTime()) && now > d;
        }
        if (act.created_at) {
            const created = new Date(String(act.created_at).replace(/-/g, '/'));
            if (!isNaN(created.getTime())) {
                const limit = new Date(created.getTime() + 10 * 24 * 60 * 60 * 1000);
                return now > limit;
            }
        }
        return false;
    });
};

const filterRequestByStatusType = (req, selectedType, dateFilter = '') => {
    if (dateFilter && !matchesDateFilter(req, dateFilter)) {
        return false;
    }

    const typeNum = Number(selectedType);
    if (typeNum === REQUEST_STATUS.ALL) return true; // Todas las solicitudes

    const statusId = Number(req?.status?.id || req?.status_id);
    const statusName = String(req?.status?.name || '').toLowerCase();

    // Canceladas / Rechazadas / Desestimadas / Vencidas / Expiradas por acción requerida
    const isCanceledOrRejected = statusId === REQUEST_STATUS.CANCELED || statusId === REQUEST_STATUS.REJECTED || statusId === REQUEST_STATUS.DISMISSED || statusId === REQUEST_STATUS.EXPIRED ||
        statusName.includes('cancelad') || statusName.includes('rechazad') || 
        statusName.includes('desestimad') || statusName.includes('vencid') || 
        statusName.includes('expirad') || isActionRequiredExpired(req);

    // Aprobadas / Completadas (Strictly NOT canceled/rejected)
    const isApproved = !isCanceledOrRejected && (
        statusId === REQUEST_STATUS.APPROVED || statusName.includes('aprobad') || statusName.includes('completad')
    );

    // Acción Requerida: Notificación u observación del analista
    const hasAction = (
        (req?.required_actions && Array.isArray(req.required_actions) && req.required_actions.length > 0) ||
        (req?.requiredActions && Array.isArray(req.requiredActions) && req.requiredActions.length > 0) ||
        (req?.request_actions !== null && req?.request_actions !== undefined)
    );

    // 1. Acción Requerida (99) - Filtro independiente
    if (typeNum === REQUEST_STATUS.ACTION_REQUIRED) {
        return hasAction;
    }

    // 2. Aprobada (3)
    if (typeNum === REQUEST_STATUS.APPROVED) {
        return isApproved;
    }

    // 3. Finalizada (7)
    if (typeNum === REQUEST_STATUS.FINISHED) {
        return isCanceledOrRejected;
    }

    // 4. En proceso (1, 20, 2, 5)
    if (typeNum === REQUEST_STATUS.IN_PROCESS || typeNum === 20 || typeNum === 2 || typeNum === REQUEST_STATUS.EXPIRED) {
        return !hasAction && !isApproved && !isCanceledOrRejected;
    }

    return true;
};

const hasRequiredActions = (req) => {
    const statusId = Number(req?.status?.id || req?.status_id);
    const statusName = String(req?.status?.name || '').toLowerCase();
    if (statusId === REQUEST_STATUS.CANCELED || statusId === REQUEST_STATUS.REJECTED || statusId === REQUEST_STATUS.APPROVED || statusName.includes('cancelad') || statusName.includes('rechazad') || statusName.includes('aprobad')) {
        return false;
    }

    const actions = req?.required_actions || req?.requiredActions || (req?.request_actions ? (Array.isArray(req.request_actions) ? req.request_actions : [req.request_actions]) : []);
    if (!actions || actions.length === 0) return false;

    const comments = req?.comments ? [...req.comments] : [];
    comments.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    const lastComment = comments.length > 0 ? comments[comments.length - 1] : null;

    if (lastComment && (lastComment.user_id === null || lastComment.user_id === undefined)) {
        return false;
    }

    const pendingActions = actions.filter(act => {
        const isCompleted = act?.status === true || act?.status === 1 || act?.status === 'true' || act?.completed === true;
        if (isCompleted) return false;
        return true;
    });

    return pendingActions.length > 0;
};

const sortRequestsWithActionsFirst = (arr) => {
    if (!arr || !Array.isArray(arr)) return [];
    return [...arr].sort((a, b) => {
        const aHas = hasRequiredActions(a) ? 1 : 0;
        const bHas = hasRequiredActions(b) ? 1 : 0;
        if (aHas !== bHas) {
            return bHas - aHas; // Las que tienen requerimiento / acción requerida van siempre primero
        }
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
};

async function fetchMergedRequests(fetchPageFn, targetPage, pageSize, filterFn) {
    try {
        const firstApiRes = await fetchPageFn(1);
        if (!firstApiRes) {
            return { data: [], last_page: 1, total: 0, per_page: pageSize, apiTotal: 0, apiLastPage: 1, apiPagesScanned: 0 };
        }

        const total = firstApiRes?.total !== undefined ? firstApiRes.total : (firstApiRes?.data ? firstApiRes.data.length : 0);
        const apiPageSize = firstApiRes?.per_page || 5;
        const apiLastPage = firstApiRes?.last_page || Math.ceil(total / apiPageSize);

        // Consultar páginas de la API en paralelo (máximo 25 páginas que permite la API para mantener velocidad)
        const pagesToFetch = Math.min(apiLastPage, 25);
        let allData = [];
        if (pagesToFetch > 0) {
            const promises = [];
            for (let p = 1; p <= pagesToFetch; p++) {
                promises.push(fetchPageFn(p).catch((err) => {
                    console.warn(`[MyRequests] Error consultando página ${p}:`, err);
                    return null;
                }));
            }
            const results = await Promise.all(promises);
            results.forEach(res => {
                if (res && res.data && Array.isArray(res.data)) {
                    allData = allData.concat(res.data);
                }
            });
        }

        // Deduplicar solicitudes por ID único
        const uniqueData = [];
        const seenIds = new Set();
        allData.forEach(item => {
            if (item && item.id && !seenIds.has(item.id)) {
                seenIds.add(item.id);
                uniqueData.push(item);
            }
        });
        
        const filteredData = filterFn ? uniqueData.filter(filterFn) : uniqueData;
        const sortedData = sortRequestsWithActionsFirst(filteredData);
        
        // Recalcular la cantidad exacta de páginas según los elementos filtrados y el pageSize (10/25/50)
        const totalMatching = sortedData.length;
        const calculatedLastPage = Math.max(1, Math.ceil(totalMatching / pageSize));
        
        const validPage = Math.max(1, Math.min(targetPage, calculatedLastPage));
        const startIndex = (validPage - 1) * pageSize;
        const slicedData = sortedData.slice(startIndex, startIndex + pageSize);
        
        return {
            data: slicedData,
            last_page: calculatedLastPage,
            total: totalMatching,
            per_page: pageSize,
            // Datos reales que reporta la API, sin importar cuántas páginas se hayan podido consultar
            apiTotal: total,
            apiLastPage: apiLastPage,
            apiPagesScanned: pagesToFetch
        };
    } catch (err) {
        console.error("Error in fetchMergedRequests:", err);
        return { data: [], last_page: 1, total: 0, per_page: pageSize, apiTotal: 0, apiLastPage: 1, apiPagesScanned: 0 };
    }
}

const hasActionForStatus = (req) => {
    if (!req) return false;
    return Boolean(
        (req?.required_actions && Array.isArray(req.required_actions) && req.required_actions.length > 0) ||
        (req?.requiredActions && Array.isArray(req.requiredActions) && req.requiredActions.length > 0) ||
        (req?.request_actions !== null && req?.request_actions !== undefined)
    );
};

async function fetchAllStatusesPersonal(citizenID) {
    try {
        const firstPage = await getRequestedServicesCitizenIDWithOutCompany(citizenID, 1, 100, '');
        const ids = new Set();
        if (firstPage && firstPage.data) {
            firstPage.data.forEach(req => {
                if (req?.status?.id !== undefined && req?.status?.id !== null) {
                    ids.add(Number(req.status.id));
                }
                if (hasActionForStatus(req)) {
                    ids.add(99);
                }
            });
        }
        const totalPages = Math.min(firstPage?.last_page || 1, 3);
        if (totalPages > 1) {
            const promises = [];
            for (let p = 2; p <= totalPages; p++) {
                promises.push(getRequestedServicesCitizenIDWithOutCompany(citizenID, p, 100, '').catch(() => null));
            }
            const results = await Promise.all(promises);
            results.forEach(res => {
                if (res && res.data) {
                    res.data.forEach(req => {
                        if (req?.status?.id !== undefined && req?.status?.id !== null) {
                            ids.add(Number(req.status.id));
                        }
                        if (hasActionForStatus(req)) {
                            ids.add(99);
                        }
                    });
                }
            });
        }
        return ids;
    } catch (err) {
        console.error("Error fetching allStatusesPersonal:", err);
        throw err;
    }
}

async function fetchAllStatusesCompany(citizenID) {
    try {
        const statusIds = new Set();
        const companyIds = new Set();
        const applicantIds = new Set();

        const collect = (req) => {
            if (req?.status?.id !== undefined && req?.status?.id !== null) {
                statusIds.add(Number(req.status.id));
            }
            if (hasActionForStatus(req)) {
                statusIds.add(99);
            }
            if (req?.company_id !== undefined && req?.company_id !== null) {
                companyIds.add(String(req.company_id));
            }
            if (req?.doc_identification) {
                applicantIds.add(String(req.doc_identification));
            }
        };

        const firstPage = await getRequestedServicesCitizenIDWithAllCompany(citizenID, 1, 1, '', 0, 0);
        if (firstPage && firstPage.data) {
            firstPage.data.forEach(collect);
        }
        const totalPages = Math.min(firstPage?.last_page || 1, 3);
        if (totalPages > 1) {
            const promises = [];
            for (let p = 2; p <= totalPages; p++) {
                promises.push(getRequestedServicesCitizenIDWithAllCompany(citizenID, p, 1, '', 0, 0).catch(() => null));
            }
            const results = await Promise.all(promises);
            results.forEach(res => {
                if (res && res.data) {
                    res.data.forEach(collect);
                }
            });
        }
        return { statusIds, companyIds, applicantIds };
    } catch (err) {
        console.error("Error fetching allStatusesCompany:", err);
        throw err;
    }
}

function MyRequests({ rolBase }) {
    const history = useHistory();
    const isMobile = useMediaQuery('(max-width:768px)');

    const [currentLang, setCurrentLang] = useState(LocalStorageService.getItem('language') || 'es');

    useEffect(() => {
        const handleLangChange = (e) => {
            if (e?.detail) {
                setCurrentLang(e.detail);
            } else {
                setCurrentLang(LocalStorageService.getItem('language') || 'es');
            }
        };
        window.addEventListener('languageChange', handleLangChange);
        return () => window.removeEventListener('languageChange', handleLangChange);
    }, []);

    const isEn = currentLang === 'en';

    const initialDataCompany = {
        value: 0,
        label: isEn ? 'All companies' : 'Todas las empresas'
    };
    const initialDataApplicant = {
        value: 0,
        label: isEn ? 'All applicants' : 'Todos los solicitantes'
    };
    const finishData = {
        value: 0,
        label: isEn ? 'No records' : 'Sin registros'
    };
    const { companyID, citizenID } = useSelector((state) => state.authReducer);

    const queryClient = useQueryClient();
    const userData = queryClient.getQueryData(['userData']);

    const { data: companiesData, isLoading: companiesLoading } = useQuery(
        ["allCompaniesData"],
        () => getAllCompanies()
    );

    const [requestType, setRequestType] = useState(99); // Por defecto: Acción Requerida
    const [requestForCompany, setRequestForCompany] = useState([]);
    const [requestForCompanyId, setRequestForCompanyId] = useState(0);
    const [requestForApplicant, setRequestForApplicant] = useState([]);
    const [requestForApplicantId, setRequestForApplicantId] = useState(0);

    const [searchModalOpen, setSearchModalOpen] = useState(false);
    const [searchModalCompanyOpen, setSearchModalCompanyOpen] = useState(false);
    const [companySelected, setCompanySelected] = useState(null);
    const [requestName, setRequestName] = useState('');
    const [requestNameCompany, setRequestNameCompany] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageCompany, setCurrentPageCompany] = useState(1);
    const [pageSizePersonal, setPageSizePersonal] = useState(10);
    const [pageSizeCompany, setPageSizeCompany] = useState(10);
    const [stringFilter, setStringFilter] = useState('');
    const [stringFilterCompany, setStringFilterCompany] = useState('');
    const [dateFilterPersonal, setDateFilterPersonal] = useState('');
    const [dateFilterCompany, setDateFilterCompany] = useState('');
    const [requestListByCompanyFilter, setRequestListByCompanyFilter] = useState([]);
    const [requestCode, setRequestCode] = useState();
    const [viewMode, setViewMode] = useState('list'); // 'list' (vista detalles actual) o 'grid' (vista cuadrícula)

    const titleRef = useRef(null);

    const [claimModalVisible, setClaimModalVisible] = useState(false);
    const [searchUser, setSearchUser] = useState(null);
    const [value, setValue] = useState('1');

    const mutationSearchUserById = useMutation(searchUserByCitizenId);
    const mutationChangeMember = useMutation(changeMember);

    const { data: allStatusesPersonal } = useQuery(
        ['allStatusesPersonal', citizenID],
        () => fetchAllStatusesPersonal(citizenID),
        { enabled: !!citizenID, staleTime: 60000, retry: false }
    );

    const { data: allStatusesCompany } = useQuery(
        ['allStatusesCompany', citizenID],
        () => fetchAllStatusesCompany(citizenID),
        { enabled: !!citizenID, staleTime: 60000, retry: false }
    );

    const { data: requestsList, isLoading: requestListIsLoading } = useQuery(
        ['requestsList', citizenID, requestType, stringFilter, dateFilterPersonal, currentPage, value, pageSizePersonal],
        async () => {
            return fetchMergedRequests(
                (p) => getRequestedServicesCitizenIDWithOutCompany(citizenID, p, 100, stringFilter),
                currentPage,
                pageSizePersonal,
                (req) => filterRequestByStatusType(req, requestType, dateFilterPersonal)
            );
        },
        { staleTime: 60000, enabled: !!citizenID }
    );

    const { data: requestListByCompany, isLoading: requestListByCompanyIsLoading } = useQuery(
        ['requestListByCompany', citizenID, requestType, stringFilterCompany, dateFilterCompany, currentPageCompany, value, requestForApplicantId, requestForCompanyId, pageSizeCompany],
        async () => {
            return fetchMergedRequests(
                (p) => getRequestedServicesCitizenIDWithAllCompany(
                    citizenID,
                    p,
                    1,
                    stringFilterCompany,
                    requestForApplicantId,
                    requestForCompanyId
                ),
                currentPageCompany,
                pageSizeCompany,
                (req) => filterRequestByStatusType(req, requestType, dateFilterCompany)
            );
        },
        { staleTime: 60000, enabled: !!citizenID }
    );

    const { data: requestListByCompanyMembers, isLoading: requestListByCompanyMembersIsLoading } = useQuery(
        ['requestListByCompanyMembers', citizenID],
        () => getRequestedServicesCitizenIDWithAllCompanyMembers(citizenID),
        { cacheTime: 0, staleTime: 0, enabled: !!citizenID }
    );

    const hasCompanies = rolBase === 2 ||
        (companiesData?.payload && companiesData.payload.length > 0) ||
        (requestListByCompany?.total > 0) ||
        (requestListByCompanyMembers?.payload?.companies && requestListByCompanyMembers.payload.companies.length > 0);

    useEffect(() => {
        if (value === '2' && !hasCompanies && !companiesLoading) {
            setValue('1');
        }
    }, [value, hasCompanies, companiesLoading]);

    useEffect(() => {
        // Si la pestaña activa no tiene solicitudes con Acción Requerida, mostrar Todas en su lugar.
        if (requestType !== 99) return;
        if (value === '2') {
            if (allStatusesCompany && !allStatusesCompany.statusIds.has(99)) {
                setRequestType(100);
            }
        } else {
            if (allStatusesPersonal && !allStatusesPersonal.has(99)) {
                setRequestType(100);
            }
        }
    }, [allStatusesPersonal, allStatusesCompany, requestType, value]);

    useEffect(() => {
        if (requestForCompanyId != 0) {
            setCurrentPageCompany(1);
        } else if (requestForApplicantId != 0) {
            setCurrentPage(1);
        }
    }, [requestForCompanyId, requestForApplicantId]);

    useEffect(() => {
        if (requestListByCompany?.data?.length > 0 && requestListByCompanyMembers?.payload?.companies?.length > 0 && value === '2') {
            setRequestForCompany(() => [initialDataCompany, ...requestListByCompanyMembers?.payload?.companies]);
            setRequestForApplicant(() => [initialDataApplicant, ...requestListByCompanyMembers?.payload?.members]);
        }
    }, [requestListByCompany, value]);

    useEffect(() => {
        setRequestForCompanyId(0);
        setRequestListByCompanyFilter([]);
    }, [requestType]);

    const totalRequestsPersonal = requestsList?.total || 0;
    const totalRequestsCompany = requestListByCompany?.total || 0;
    const personalHasMorePages = Boolean(requestsList && requestsList.apiLastPage > requestsList.apiPagesScanned);
    const companyHasMorePages = Boolean(requestListByCompany && requestListByCompany.apiLastPage > requestListByCompany.apiPagesScanned);

    const personalEmptyTitle = personalHasMorePages ? "No se encontraron coincidencias en el rango consultado" : "No se encontraron registros";
    const personalEmptySubtitle = personalHasMorePages
        ? `Solo se consultaron las primeras ${requestsList?.apiPagesScanned} de ${requestsList?.apiLastPage} páginas disponibles en la API (${requestsList?.apiTotal} solicitudes en total). Puede haber solicitudes que coincidan con este filtro más adelante en el historial que aún no se han revisado.`
        : "No existen solicitudes activas que coincidan con sus criterios de búsqueda en este momento.";
    const companyEmptyTitle = companyHasMorePages ? "No se encontraron coincidencias en el rango consultado" : "No se encontraron registros";
    const companyEmptySubtitle = companyHasMorePages
        ? `Solo se consultaron las primeras ${requestListByCompany?.apiPagesScanned} de ${requestListByCompany?.apiLastPage} páginas disponibles en la API (${requestListByCompany?.apiTotal} solicitudes en total). Puede haber solicitudes que coincidan con este filtro más adelante en el historial que aún no se han revisado.`
        : "No existen solicitudes activas que coincidan con sus criterios de búsqueda en este momento.";

    // Fallback: extract status IDs from currently visible requests in the main list
    const visibleStatusesPersonal = new Set();
    (requestsList?.data || []).forEach(req => {
        if (req?.status?.id !== undefined && req?.status?.id !== null) {
            visibleStatusesPersonal.add(Number(req.status.id));
        }
        if (hasRequiredActions(req)) {
            visibleStatusesPersonal.add(99);
        }
    });

    const visibleStatusesCompany = new Set();
    (requestListByCompany?.data || []).forEach(req => {
        if (req?.status?.id !== undefined && req?.status?.id !== null) {
            visibleStatusesCompany.add(Number(req.status.id));
        }
        if (hasRequiredActions(req)) {
            visibleStatusesCompany.add(99);
        }
    });

    const filteredFilterTypesPersonal = getRequestFilterTypes();
    // En la pestaña Empresa solo se muestran las opciones de status que realmente
    // tienen solicitudes (según los estados vistos al escanear las solicitudes de empresa).
    const filteredFilterTypesCompany = getRequestFilterTypes().filter((type) => {
        if (type.value === 100) return true; // "Todas" siempre visible
        if (!allStatusesCompany) return true; // aún cargando, no ocultar de más
        const idsForType = STATUS_IDS_BY_FILTER_TYPE[type.value] || [];
        return idsForType.some((id) => allStatusesCompany.statusIds.has(id));
    });

    // "Filtro por empresa" y "Por solicitante" solo muestran empresas/solicitantes
    // que realmente tienen solicitudes (según lo visto al escanear las solicitudes de empresa).
    const filteredRequestForCompany = requestForCompany.filter((item) =>
        item.value === 0 || !allStatusesCompany || allStatusesCompany.companyIds.has(String(item.value))
    );
    const filteredRequestForApplicant = requestForApplicant.filter((item) =>
        item.value === 0 || !allStatusesCompany || allStatusesCompany.applicantIds.has(String(item.value))
    );

    const handleChangeTabs = (event, newValue) => {
        setValue(newValue);
        setRequestType(99);
    };

    const searchUserById = (formData) => {
        mutationSearchUserById.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    setSearchUser(data.payload);
                    // refresh cache of searchUserId
                    queryClient.invalidateQueries('searchUserId');
                } else {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'info'
                    });
                }
            }
        });
    };

    const handleTypeChange = (value) => {
        setCurrentPage(1);
        setCurrentPageCompany(1);
        setRequestType(value.target.value);
        setRequestName('');
        titleRef.current?.scrollIntoView();
    };

    const handleTypeChangeCompany = (value) => {
        setRequestForCompanyId(value.target.value);
    };

    const handleTypeChangeApplicant = (value) => {
        setRequestForApplicantId(value.target.value);
    };

    const handlePageChange = (value, type) => {
        if (type == 'personal') {
            setCurrentPage(value);
        } else {
            setCurrentPageCompany(value);
        }
        titleRef.current?.scrollIntoView();
    };

    const handlePageSizeChange = (newSize, type) => {
        if (type === 'personal') {
            setPageSizePersonal(newSize);
            setCurrentPage(1);
        } else {
            setPageSizeCompany(newSize);
            setCurrentPageCompany(1);
        }
    };

    const handleStringFilter = (type) => {
        if (stringFilter === 0 || stringFilterCompany === 0) handlePageChange(1, type);
        if (type == 'personal') {
            setStringFilter(requestName);
        } else {
            setStringFilterCompany(requestNameCompany);
        }
    };

    const handleRemoveFilters = (type) => {
        if (type == 'personal') {
            setStringFilter('');
            setRequestName('');
        } else {
            setStringFilterCompany('');
            setRequestNameCompany('');
        }
    };

    const handleClaimModalVisibility = () => {
        setSearchUser(null);
        setClaimModalVisible(!claimModalVisible);
    };

    const deleteFoundUser = () => {
        setSearchUser(null);
    };

    const handlerChangeOwner = (citizenID) => {
        changeOwner({ request_code: requestCode, new_citizen_id: citizenID, company_id: companySelected });
    };

    const changeOwner = (formData) => {
        mutationChangeMember.mutate(formData, {
            onSuccess: (data) => {
                if (data.success) {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'success'
                    }).then((result) => {
                        if (result.isConfirmed) {
                            setSearchUser(null);
                            setRequestCode(null);
                            handleClaimModalVisibility();
                        }
                    });
                    // refresh cache of inviteUser
                    queryClient.invalidateQueries('requestListByCompany');
                    queryClient.invalidateQueries('allStatusesCompany');
                } else {
                    Swal.fire({
                        title: 'Información',
                        text: data.msg,
                        icon: 'info'
                    });
                }
            }
        });
    };

    const isInitialLoading = companiesLoading || requestListIsLoading || (hasCompanies && (requestListByCompanyIsLoading || requestListByCompanyMembersIsLoading));

    // El switcher Personal/Empresa debe aparecer siempre que el ciudadano tenga alguna
    // vinculación con empresas, sin importar si el filtro de status actual muestra
    // resultados o no dentro de esa pestaña (eso lo maneja el EmptyState de cada tab).
    const showPersonalTab = true;
    const showCompanyTab = hasCompanies;
    const showTabHeader = hasCompanies;

    return isInitialLoading ? <CenterLoading /> : (
        <RequestsWrapper ref={titleRef} style={{ width: '100%' }}>
            <TabContext value={value}>
                {showTabHeader && (
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            marginBottom: 3,
                            padding: { xs: '16px 12px 0 12px', md: '32px 32px 0 32px' }
                        }}
                    >
                        <TabList
                            TabIndicatorProps={{ style: { background: COLORS.secondary, height: 3 } }}
                            onChange={handleChangeTabs}
                            aria-label="solicitudes tabs"
                        >
                            {showPersonalTab && (
                                <Tab
                                    label={isEn ? "Personal" : "Personal"}
                                    style={{
                                        color: value === '1' ? COLORS.secondary : COLORS.secondaryOpa,
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        fontWeight: 'bold',
                                        fontSize: 16
                                    }}
                                    value="1"
                                />
                            )}
                            {showCompanyTab && (
                                <Tab
                                    label={isEn ? "Company" : "Empresa"}
                                    style={{
                                        color: value === '2' ? COLORS.secondary : COLORS.secondaryOpa,
                                        fontFamily: FONTS_FAMILY.bold.body,
                                        fontWeight: 'bold',
                                        fontSize: 16
                                    }}
                                    value="2"
                                />
                            )}
                        </TabList>
                    </Box>
                )}
                <TabPanel value="1" sx={{ padding: showTabHeader ? { xs: '8px 4px 16px 4px', md: '0 32px 32px 32px' } : { xs: '12px 4px', md: '32px' } }}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            {/* Filters Row */}
                            {isMobile ? (
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                                    {/* Mobile Search Icon Button */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>{isEn ? "Search" : "Buscar"}</span>
                                        <Badge color="error" variant="dot" invisible={!stringFilter.length} sx={{ '& .MuiBadge-badge': { top: 4, right: 4 } }}>
                                            <IconButton
                                                onClick={() => setSearchModalOpen(true)}
                                                sx={{
                                                    bgcolor: stringFilter.length > 0 ? '#0F2350' : '#FFFFFF',
                                                    color: stringFilter.length > 0 ? '#FFFFFF' : '#0F2350',
                                                    border: '1px solid #CFDCE0',
                                                    borderRadius: '8px',
                                                    width: '42px',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    p: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: 'none',
                                                    '&:hover': { bgcolor: '#0F2350', color: '#FFFFFF' }
                                                }}
                                            >
                                                <SearchIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                        </Badge>
                                    </div>

                                    {/* Mobile Status Filter */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            '& .Row': { display: 'none' },
                                            '& .MuiFormControl-root': { margin: '0 !important', marginTop: '0 !important' },
                                            '& .MuiFormHelperText-root': { display: 'none !important' },
                                            '& .MuiOutlinedInput-root': {
                                                height: '42px',
                                                minHeight: '42px',
                                                borderRadius: '8px',
                                                borderColor: '#CFDCE0',
                                                marginTop: '0 !important'
                                            },
                                            '& .MuiSelect-select': {
                                                padding: '8px 12px',
                                                height: '42px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                boxSizing: 'border-box',
                                                fontSize: '14px',
                                                color: '#0F2350',
                                                fontWeight: 500
                                            }
                                        }}
                                    >
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>{isEn ? "Filter by status" : "Filtro por status"}</span>
                                        <Select
                                            title=""
                                            id="filterType"
                                            data={filteredFilterTypesPersonal}
                                            value={requestType}
                                            onChange={handleTypeChange}
                                            disableEmptyValue
                                        />
                                    </Box>

                                    {/* Mobile View Toggle */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>{isEn ? "View" : "Vista"}</span>
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(e, nextView) => { if (nextView !== null) setViewMode(nextView); }}
                                            size="small"
                                            sx={{
                                                height: '42px',
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '8px',
                                                border: '1px solid #CFDCE0',
                                                p: '2px',
                                                boxSizing: 'border-box',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    px: 1,
                                                    height: '36px',
                                                    color: '#64748B',
                                                    '&.Mui-selected': {
                                                        bgcolor: '#192873',
                                                        color: '#FFFFFF !important',
                                                        '&:hover': { bgcolor: '#192873' },
                                                        '& svg': { color: '#FFFFFF !important' }
                                                    }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="list" aria-label="vista detalles">
                                                <FormatListBulletedIcon sx={{ fontSize: '18px' }} />
                                            </ToggleButton>
                                            <ToggleButton value="grid" aria-label="vista cuadricula">
                                                <GridViewIcon sx={{ fontSize: '18px' }} />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '24px', width: '100%', alignItems: 'flex-start' }}>
                                    {/* Search filter */}
                                    <div style={{ flex: 1, minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>{isEn ? "Quick search" : "Búsqueda rápida"}</span>
                                        <TextField
                                            placeholder={isEn ? "Application name or number" : "Nombre o número de solicitud"}
                                            type="text"
                                            id="requestID"
                                            value={requestName}
                                            onChange={(e) => setRequestName(e.target.value)}
                                            helperText=""
                                            style={{ width: '100%' }}
                                            endAdornment={
                                                stringFilter.length > 0 ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => handleRemoveFilters('personal')}
                                                            aria-label="delete"
                                                            size="small"
                                                        >
                                                            <CloseIcon
                                                                titleAccess={isEn ? "Remove filter" : "Eliminar filtro"}
                                                                color="error"
                                                                sx={{ fontSize: '1.2em' }}
                                                            />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => handleStringFilter('personal')}
                                                            aria-label="search"
                                                            size="small"
                                                        >
                                                            <SearchIcon
                                                                titleAccess={isEn ? "search" : "buscar"}
                                                                color="action"
                                                                sx={{ fontSize: '1.2em' }}
                                                            />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }
                                        />
                                    </div>

                                    {/* Status filter */}
                                    <div style={{ minWidth: '200px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>{isEn ? "Filter by status" : "Filtro por status"}</span>
                                        <Select
                                            title=""
                                            id="filterType"
                                            data={filteredFilterTypesPersonal}
                                            value={requestType}
                                            onChange={handleTypeChange}
                                            disableEmptyValue
                                        />
                                    </div>

                                    {/* Date filter */}
                                    <div style={{ minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>{isEn ? "Filter by date" : "Filtro por fecha"}</span>
                                        <TextField
                                            type="date"
                                            id="dateFilterPersonal"
                                            value={dateFilterPersonal}
                                            onChange={(e) => {
                                                setCurrentPage(1);
                                                setDateFilterPersonal(e.target.value);
                                            }}
                                            helperText=""
                                            InputLabelProps={{ shrink: true }}
                                            style={{ width: '100%' }}
                                            endAdornment={
                                                dateFilterPersonal ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => {
                                                                setCurrentPage(1);
                                                                setDateFilterPersonal('');
                                                            }}
                                                            aria-label="limpiar fecha"
                                                            size="small"
                                                        >
                                                            <CloseIcon color="error" sx={{ fontSize: '1.2em' }} />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : null
                                            }
                                        />
                                    </div>

                                    {/* View Mode Toggle */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>{isEn ? "View" : "Vista"}</span>
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(e, nextView) => { if (nextView !== null) setViewMode(nextView); }}
                                            size="small"
                                            sx={{
                                                height: '46px',
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '7px',
                                                border: '1px solid #CFDCE0',
                                                p: '2px',
                                                boxSizing: 'border-box',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '5px',
                                                    px: 1.8,
                                                    height: '40px',
                                                    color: '#64748B',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '13.5px',
                                                    '&.Mui-selected': {
                                                        bgcolor: '#192873',
                                                        color: '#FFFFFF !important',
                                                        '&:hover': { bgcolor: '#192873' },
                                                        '& svg': { color: '#FFFFFF !important' }
                                                    },
                                                    '&:hover': { bgcolor: 'rgba(25, 40, 115, 0.06)' }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="list" aria-label="vista detalles">
                                                <FormatListBulletedIcon sx={{ fontSize: '18px', mr: 0.5 }} />
                                                {isEn ? "Details" : "Detalles"}
                                            </ToggleButton>
                                            <ToggleButton value="grid" aria-label="vista cuadricula">
                                                <GridViewIcon sx={{ fontSize: '18px', mr: 0.5 }} />
                                                {isEn ? "Grid" : "Cuadrícula"}
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            )}

                        <ListContainer>
                            <div
                                style={{
                                    display: viewMode === 'grid' ? 'grid' : 'flex',
                                    flexDirection: viewMode === 'grid' ? 'unset' : 'column',
                                    gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(320px, 1fr))' : '1fr',
                                    gridAutoRows: viewMode === 'grid' ? '1fr' : 'auto',
                                    gap: viewMode === 'grid' ? '20px' : '0px',
                                    width: '100%'
                                }}
                            >
                                {requestsList?.data?.map((request, index) => (
                                    <Fragment key={request.id}>
                                        {viewMode === 'list' && index > 0 && <SmallHeightDivider />}
                                        <RequestCard
                                            isGridView={viewMode === 'grid'}
                                            title={request.service.name}
                                            projectName={request.nmtramite}
                                            percent={request.progress + '%'}
                                            date={request.created_at}
                                            updatedAt={request.updated_at}
                                            approvalNumber={request.approval_number}
                                            rolBase={rolBase}
                                            company_user_rol={request.user_rol_company}
                                            onClaimModalVisibility={handleClaimModalVisibility}
                                            requestCode={request.code}
                                            citizenID={citizenID}
                                            status={request.status.name}
                                            actionRequired={request.request_actions}
                                            requiredActionMessage={request.required_actions?.[0]?.message}
                                            docIdentification={request.doc_identification}
                                            companyName={request.company_name}
                                            companyRnc={request.company_rnc}
                                            onClick={() => {
                                                const queryParams = new URLSearchParams({
                                                    doc_identification: request.doc_identification,
                                                    user_rol_company: rolBase,
                                                    status_id: request.status.id
                                                }).toString();

                                                return history.push(`/app/serviceRequestedDetails/${request.id}?${queryParams}`);
                                            }}
                                            statusID={request.status.id}
                                            userName={`${request.user_name || ''} ${request.user_first_last_name || ''} ${request.user_last_name || ''}`.trim()}
                                            solution={request.solution}
                                            solutionColor={request.status.color}
                                        />
                                    </Fragment>
                                ))}
                            </div>

                            <MediumHeightDivider />
                            {requestsList?.data?.length > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
                                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '240px' }}>
                                        <StyledPagination
                                            count={requestsList?.last_page}
                                            page={currentPage}
                                            onChange={(event, page) => {
                                                handlePageChange(page, 'personal');
                                            }}
                                            variant="outlined"
                                            shape="rounded"
                                            sx={{ color: COLORS.primary }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#64748B', fontWeight: 500, marginLeft: 'auto' }}>
                                        <span>Mostrar:</span>
                                        <select
                                            value={pageSizePersonal}
                                            onChange={(e) => handlePageSizeChange(Number(e.target.value), 'personal')}
                                            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #CFDCE0', outline: 'none', backgroundColor: '#FFFFFF', color: '#0F2350', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                                        >
                                            <option value={10}>10 por página</option>
                                            <option value={25}>25 por página</option>
                                            <option value={50}>50 por página</option>
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <EmptyState
                                    title={personalEmptyTitle}
                                    subtitle={personalEmptySubtitle}
                                    onRefresh={() => {
                                        queryClient.invalidateQueries('requestsList');
                                        queryClient.invalidateQueries('allStatusesPersonal');
                                    }}
                                />
                            )}

                            <MediumHeightDivider />
                        </ListContainer>
                        </div>
                </TabPanel>
                {hasCompanies && (
                    <TabPanel value="2" sx={{ padding: showTabHeader ? { xs: '8px 4px 16px 4px', md: '0 32px 32px 32px' } : { xs: '12px 4px', md: '32px' } }}>
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                            {/* Filters Row */}
                            {isMobile ? (
                                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', marginBottom: '20px', width: '100%' }}>
                                    {/* Mobile Search Icon Button */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>Buscar</span>
                                        <Badge color="error" variant="dot" invisible={!stringFilterCompany.length} sx={{ '& .MuiBadge-badge': { top: 4, right: 4 } }}>
                                            <IconButton
                                                onClick={() => setSearchModalCompanyOpen(true)}
                                                sx={{
                                                    bgcolor: stringFilterCompany.length > 0 ? '#0F2350' : '#FFFFFF',
                                                    color: stringFilterCompany.length > 0 ? '#FFFFFF' : '#0F2350',
                                                    border: '1px solid #CFDCE0',
                                                    borderRadius: '8px',
                                                    width: '42px',
                                                    height: '42px',
                                                    boxSizing: 'border-box',
                                                    p: 0,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    boxShadow: 'none',
                                                    '&:hover': { bgcolor: '#0F2350', color: '#FFFFFF' }
                                                }}
                                            >
                                                <SearchIcon sx={{ fontSize: '20px' }} />
                                            </IconButton>
                                        </Badge>
                                    </div>

                                    {/* Mobile Status Filter */}
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: '4px',
                                            '& .Row': { display: 'none' },
                                            '& .MuiFormControl-root': { margin: '0 !important', marginTop: '0 !important' },
                                            '& .MuiFormHelperText-root': { display: 'none !important' },
                                            '& .MuiOutlinedInput-root': {
                                                height: '42px',
                                                minHeight: '42px',
                                                borderRadius: '8px',
                                                borderColor: '#CFDCE0',
                                                marginTop: '0 !important'
                                            },
                                            '& .MuiSelect-select': {
                                                padding: '8px 12px',
                                                height: '42px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                boxSizing: 'border-box',
                                                fontSize: '14px',
                                                color: '#0F2350',
                                                fontWeight: 500
                                            }
                                        }}
                                    >
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>Filtro por status</span>
                                        <Select
                                            title=""
                                            id="filterTypeCompany"
                                            data={filteredFilterTypesCompany}
                                            value={requestType}
                                            onChange={handleTypeChange}
                                            disableEmptyValue
                                        />
                                    </Box>

                                    {/* Mobile View Toggle */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '11px', color: '#64748B', fontWeight: '700', height: '16px', lineHeight: '16px', margin: 0 }}>Vista</span>
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(e, nextView) => { if (nextView !== null) setViewMode(nextView); }}
                                            size="small"
                                            sx={{
                                                height: '42px',
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '8px',
                                                border: '1px solid #CFDCE0',
                                                p: '2px',
                                                boxSizing: 'border-box',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    px: 1,
                                                    height: '36px',
                                                    color: '#64748B',
                                                    '&.Mui-selected': {
                                                        bgcolor: '#192873',
                                                        color: '#FFFFFF !important',
                                                        '&:hover': { bgcolor: '#192873' },
                                                        '& svg': { color: '#FFFFFF !important' }
                                                    }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="list" aria-label="vista detalles">
                                                <FormatListBulletedIcon sx={{ fontSize: '18px' }} />
                                            </ToggleButton>
                                            <ToggleButton value="grid" aria-label="vista cuadricula">
                                                <GridViewIcon sx={{ fontSize: '18px' }} />
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px', width: '100%', alignItems: 'flex-start' }}>
                                    {/* Search filter */}
                                    <div style={{ flex: 1, minWidth: '220px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>Búsqueda rápida</span>
                                        <TextField
                                            placeholder="Nombre o número de solicitud"
                                            type="text"
                                            id="requestIDCompany"
                                            value={requestNameCompany}
                                            onChange={(e) => setRequestNameCompany(e.target.value)}
                                            helperText=""
                                            style={{ width: '100%' }}
                                            endAdornment={
                                                stringFilterCompany.length > 0 ? (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => handleRemoveFilters('company')}
                                                            aria-label="delete"
                                                            size="small"
                                                        >
                                                            <CloseIcon
                                                                titleAccess="Eliminar filtro"
                                                                color="error"
                                                                sx={{ fontSize: '1.2em' }}
                                                            />
                                                        </IconButton>
                                                    </InputAdornment>
                                                ) : (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => handleStringFilter('company')}
                                                            aria-label="search"
                                                            size="small"
                                                        >
                                                            <SearchIcon
                                                                titleAccess="buscar"
                                                                color="action"
                                                                sx={{ fontSize: '1.2em' }}
                                                            />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }
                                        />
                                    </div>

                                    {/* Company filter */}
                                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>Filtro por empresa</span>
                                        <Select
                                            title=""
                                            id="filterCompany"
                                            data={filteredRequestForCompany.length == 0 ? [finishData] : filteredRequestForCompany}
                                            value={requestForCompanyId}
                                            onChange={handleTypeChangeCompany}
                                            disableEmptyValue
                                        />
                                    </div>

                                    {/* Applicant filter */}
                                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>Por solicitante</span>
                                        <Select
                                            title=""
                                            id="filterSolicitante"
                                            data={filteredRequestForApplicant.length == 0 ? [finishData] : filteredRequestForApplicant}
                                            value={requestForApplicantId}
                                            onChange={handleTypeChangeApplicant}
                                            disableEmptyValue
                                        />
                                    </div>

                                    {/* Status filter */}
                                    <div style={{ minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>Por status</span>
                                        <Select
                                            title=""
                                            id="filterTypeCompany"
                                            data={filteredFilterTypesCompany}
                                            value={requestType}
                                            onChange={handleTypeChange}
                                            disableEmptyValue
                                        />
                                    </div>

                                    {/* View Mode Toggle */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', textTransform: 'none', height: '16px', lineHeight: '16px' }}>Vista</span>
                                        <ToggleButtonGroup
                                            value={viewMode}
                                            exclusive
                                            onChange={(e, nextView) => { if (nextView !== null) setViewMode(nextView); }}
                                            size="small"
                                            sx={{
                                                height: '46px',
                                                bgcolor: '#FFFFFF',
                                                borderRadius: '7px',
                                                border: '1px solid #CFDCE0',
                                                p: '2px',
                                                boxSizing: 'border-box',
                                                '& .MuiToggleButton-root': {
                                                    border: 'none',
                                                    borderRadius: '5px',
                                                    px: 1.8,
                                                    height: '40px',
                                                    color: '#64748B',
                                                    textTransform: 'none',
                                                    fontWeight: 600,
                                                    fontSize: '13.5px',
                                                    '&.Mui-selected': {
                                                        bgcolor: '#192873',
                                                        color: '#FFFFFF !important',
                                                        '&:hover': { bgcolor: '#192873' },
                                                        '& svg': { color: '#FFFFFF !important' }
                                                    },
                                                    '&:hover': { bgcolor: 'rgba(25, 40, 115, 0.06)' }
                                                }
                                            }}
                                        >
                                            <ToggleButton value="list" aria-label="vista detalles">
                                                <FormatListBulletedIcon sx={{ fontSize: '18px', mr: 0.5 }} />
                                                Detalles
                                            </ToggleButton>
                                            <ToggleButton value="grid" aria-label="vista cuadricula">
                                                <GridViewIcon sx={{ fontSize: '18px', mr: 0.5 }} />
                                                Cuadrícula
                                            </ToggleButton>
                                        </ToggleButtonGroup>
                                    </div>
                                </div>
                            )}

                        <ListContainer>
                            <div
                                style={{
                                    display: viewMode === 'grid' ? 'grid' : 'flex',
                                    flexDirection: viewMode === 'grid' ? 'unset' : 'column',
                                    gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(320px, 1fr))' : '1fr',
                                    gridAutoRows: viewMode === 'grid' ? '1fr' : 'auto',
                                    gap: viewMode === 'grid' ? '20px' : '0px',
                                    width: '100%'
                                }}
                            >
                                {requestListByCompany?.data?.map((request, index) => (
                                    <Fragment key={request?.id}>
                                        {viewMode === 'list' && index > 0 && <SmallHeightDivider />}
                                        <RequestCard
                                            isGridView={viewMode === 'grid'}
                                            title={request.service.name}
                                            projectName={request.nmtramite}
                                            percent={request.progress + '%'}
                                            date={request.created_at}
                                            updatedAt={request.updated_at}
                                            approvalNumber={request.approval_number}
                                            rolBase={rolBase}
                                            company_user_rol={request.user_rol_company}
                                            onClaimModalVisibility={(value) => handleClaimModalVisibility(value)}
                                            requestCode={request.code}
                                            citizenID={citizenID}
                                            status={request.status.name}
                                            actionRequired={request.request_actions}
                                            requiredActionMessage={request.required_actions?.[0]?.message}
                                            docIdentification={request.doc_identification}
                                            handleCompanySelected={setCompanySelected}
                                            code={request.code}
                                            setRequestCode={setRequestCode}
                                            companyID={request.company_id}
                                            companyName={request.company_name}
                                            companyRnc={request.company_rnc}
                                            onClick={() => {
                                                const queryParams = new URLSearchParams({
                                                    doc_identification: request.doc_identification,
                                                    user_rol_company: request.user_rol_company,
                                                    status_id: request.status.id,
                                                    company_id: request.company_id,
                                                    company_name: request.company_name
                                                }).toString();

                                                return history.push(`/app/serviceRequestedDetails/${request.id}?${queryParams}`);
                                            }}
                                            userName={`${request.user_name || ''} ${request.user_first_last_name || ''} ${request.user_last_name || ''}`.trim()}
                                            statusID={request.status.id}
                                            solution={request.solution}
                                            solutionColor={request.status.color}
                                        />
                                    </Fragment>
                                ))}
                            </div>

                            <MediumHeightDivider />
                            {requestListByCompany?.data?.length > 0 ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '16px', marginTop: '12px' }}>
                                    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '240px' }}>
                                        <StyledPagination
                                            count={requestListByCompany?.last_page}
                                            page={currentPageCompany}
                                            onChange={(event, page) => {
                                                handlePageChange(page, 'company');
                                            }}
                                            variant="outlined"
                                            shape="rounded"
                                            sx={{ color: COLORS.primary }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13.5px', color: '#64748B', fontWeight: 500, marginLeft: 'auto' }}>
                                        <span>Mostrar:</span>
                                        <select 
                                            value={pageSizeCompany} 
                                            onChange={(e) => handlePageSizeChange(Number(e.target.value), 'company')}
                                            style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #CFDCE0', outline: 'none', backgroundColor: '#FFFFFF', color: '#0F2350', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
                                        >
                                            <option value={10}>10 por página</option>
                                            <option value={25}>25 por página</option>
                                            <option value={50}>50 por página</option>
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <EmptyState
                                    title={companyEmptyTitle}
                                    subtitle={companyEmptySubtitle}
                                    onRefresh={() => {
                                        queryClient.invalidateQueries('requestListByCompany');
                                        queryClient.invalidateQueries('allStatusesCompany');
                                    }}
                                />
                            )}

                            <MediumHeightDivider />
                        </ListContainer>
                        </div>
                    </TabPanel>
                )}
            </TabContext>
            <DialogChangeApplicant
                claimModalVisible={claimModalVisible}
                handleClaimModalVisibility={handleClaimModalVisibility}
                companySelected={companySelected}
                searchUser={searchUser}
                deleteFoundUser={deleteFoundUser}
                onClick={handlerChangeOwner}
                title="Cambiar propietario"
                sendButtonLabel={'ENVIAR'}
            />

            {/* Mobile Personal Search Modal */}
            <Dialog
                open={searchModalOpen}
                onClose={() => setSearchModalOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '16px'
                    }
                }}
            >
                <DialogTitle sx={{ p: 0, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Merriweather, Georgia, serif', fontWeight: 700, fontSize: '18px', color: '#0F2350' }}>
                        Buscar Solicitud
                    </span>
                    <IconButton size="small" onClick={() => setSearchModalOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, mb: 2 }}>
                    <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', display: 'block', marginBottom: '8px' }}>
                        Nombre o número de solicitud
                    </span>
                    <TextField
                        autoFocus
                        placeholder="Ej. 001326 o Franja Marítima"
                        type="text"
                        id="modalRequestID"
                        value={requestName}
                        onChange={(e) => setRequestName(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 0, gap: 1 }}>
                    {stringFilter.length > 0 && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                handleRemoveFilters('personal');
                                setSearchModalOpen(false);
                            }}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Limpiar
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleStringFilter('personal');
                            setSearchModalOpen(false);
                        }}
                        sx={{
                            bgcolor: '#0F2350',
                            color: '#FFF',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            flex: 1,
                            '&:hover': { bgcolor: '#EA361A' }
                        }}
                    >
                        Buscar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mobile Company Search Modal */}
            <Dialog
                open={searchModalCompanyOpen}
                onClose={() => setSearchModalCompanyOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{
                    style: {
                        borderRadius: '16px',
                        padding: '16px'
                    }
                }}
            >
                <DialogTitle sx={{ p: 0, mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontFamily: 'Merriweather, Georgia, serif', fontWeight: 700, fontSize: '18px', color: '#0F2350' }}>
                        Buscar Solicitud
                    </span>
                    <IconButton size="small" onClick={() => setSearchModalCompanyOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, mb: 2 }}>
                    <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '12px', color: '#64748B', fontWeight: '700', display: 'block', marginBottom: '8px' }}>
                        Nombre o número de solicitud
                    </span>
                    <TextField
                        autoFocus
                        placeholder="Ej. 001326 o Franja Marítima"
                        type="text"
                        id="modalRequestIDCompany"
                        value={requestNameCompany}
                        onChange={(e) => setRequestNameCompany(e.target.value)}
                        style={{ width: '100%' }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 0, gap: 1 }}>
                    {stringFilterCompany.length > 0 && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => {
                                handleRemoveFilters('company');
                                setSearchModalCompanyOpen(false);
                            }}
                            sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                        >
                            Limpiar
                        </Button>
                    )}
                    <Button
                        variant="contained"
                        onClick={() => {
                            handleStringFilter('company');
                            setSearchModalCompanyOpen(false);
                        }}
                        sx={{
                            bgcolor: '#0F2350',
                            color: '#FFF',
                            borderRadius: '10px',
                            textTransform: 'none',
                            fontWeight: 700,
                            flex: 1,
                            '&:hover': { bgcolor: '#EA361A' }
                        }}
                    >
                        Buscar
                    </Button>
                </DialogActions>
            </Dialog>
        </RequestsWrapper>
    );
}

export default MyRequests;
